/* eslint-disable @typescript-eslint/no-explicit-any */
import { Modal } from '@design-system/components/modal/modal';
import { useEffect, useState } from 'react';
import { legacyEditWorkoutVM } from '@client/new/pages/workout/WorkoutEdit';
import { InfoBox } from './InfoBox';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { Pill } from '@client/new/pages/promotions/view/components/Pill';
import {
  defaultHIITSettings,
  defaultTabataSettings,
  ExerciseSettings,
} from '../../../view-model/workouts.domain';
import { MenuSelect } from '@client/new/pages/promotions/view/components/MenuSelect';
import { TimeInput } from '@design-system/components/time-input';

type AutoplayedFormMode = 'create' | 'edit';

export const AutoplayedSettingsModal = () => {
  const vm = legacyEditWorkoutVM.useValue();
  const [modalState, setModalState] = useState<ExerciseSettings>(
    defaultTabataSettings,
  );
  const [modalMode, setModalMode] = useState<AutoplayedFormMode>('create');

  const handleSubmit = () => {
    vm.dispatch(
      modalMode === 'create' ? 'createAutoplayed' : 'applyAutoplayedSettings',
      modalState,
    );
  };

  useEffect(() => {
    if (!vm.autoplayedSettingsModal.value) {
      setModalMode('create');
      updateType('Tabata');
      return;
    }
    setModalMode('edit');
    setModalState(vm.autoplayedSettingsModal.value);
  }, [vm.autoplayedSettingsModal]);

  const updateType = (v: ExerciseSettings['type']) =>
    setModalState(v === 'Tabata' ? defaultTabataSettings : defaultHIITSettings);
  const title =
    modalMode === 'create'
      ? 'Autoplay Workout'
      : `Edit ${modalState.type} format`;
  return (
    <Modal
      hideCloseButton
      isOpen={vm.autoplayedSettingsModal.isOpen}
      onClose={() => vm.dispatch('hideAutoplayedSettingsModal')}
      secondaryCta={{
        caption: 'Cancel',
        onClick: () => vm.dispatch('hideAutoplayedSettingsModal'),
      }}
      primaryCta={{
        caption: modalMode === 'create' ? 'Create' : 'Save changes',
        onClick: handleSubmit,
      }}
      disableBackdropClick={true}
      largeTitle
      stretchCta
    >
      <Col gap="l">
        <Typography variant="body" size="large" fontWeight="medium">
          {title}
        </Typography>
        {modalMode === 'create' && (
          <>
            <Typography variant="body" size="medium" fontWeight="normal">
              Default settings apply automatically but can be changed later or
              per exercise—making workout creation easier!
            </Typography>
            <WorkoutTypeSelector
              selectedType={modalState.type}
              onTypeChange={updateType}
            />
          </>
        )}

        <div className="grid gap-4">
          <Row
            spreadEvenly
            alignCenter
            style={{ display: 'grid', gridTemplateColumns: '1fr 85px' }}
          >
            <Typography variant="body" size="medium">
              Time per set in Exercise:
            </Typography>
            <TimeInput
              min={1}
              max={1000}
              value={modalState.time}
              onChange={(e: { target: { value: any } }) => {
                setModalState((s) => ({ ...s, time: e.target.value }));
              }}
            />
          </Row>

          <Row
            spreadEvenly
            alignCenter
            style={{ display: 'grid', gridTemplateColumns: '1fr 85px' }}
          >
            <Typography variant="body" size="medium">
              Rest per set in Exercise:
            </Typography>
            <TimeInput
              min={0}
              max={1000}
              value={modalState.rest}
              onChange={(e: { target: { value: any } }) => {
                setModalState((s) => ({ ...s, rest: e.target.value }));
              }}
            />
          </Row>

          {modalState.type === 'Tabata' && (
            <Row
              spreadEvenly
              alignCenter
              style={{ display: 'grid', gridTemplateColumns: '1fr 85px' }}
            >
              <Typography variant="body" size="medium">
                Sets per exercise:
              </Typography>

              <MenuSelect
                name="sets"
                options={new Array(20).fill(0).map((_, i) => ({
                  label: `${i + 1}`,
                  value: i + 1,
                }))}
                onChange={(e: { target: { value: any } }) => {
                  setModalState((s) => ({
                    ...s,
                    sets: parseInt(e.target.value, 10),
                  }));
                }}
                value={modalState.sets}
              />
            </Row>
          )}

          {modalState.type === 'HIIT' && modalMode === 'create' && (
            <Row
              spreadEvenly
              alignCenter
              style={{ display: 'grid', gridTemplateColumns: '1fr 85px' }}
            >
              <Typography variant="body" size="medium">
                Number of rounds:
              </Typography>

              <MenuSelect
                name="rounds"
                options={new Array(20).fill(0).map((_, i) => ({
                  label: `${i + 1}`,
                  value: i + 1,
                }))}
                onChange={(e: { target: { value: any } }) => {
                  setModalState((s) => ({
                    ...s,
                    rounds: parseInt(e.target.value, 10),
                  }));
                }}
                value={modalState.rounds}
              />
            </Row>
          )}
        </div>

        <WorkoutDescription />
      </Col>
    </Modal>
  );
};

const WorkoutTypeSelector = ({
  selectedType,
  onTypeChange,
}: {
  selectedType: ExerciseSettings['type'];
  onTypeChange: (type: ExerciseSettings['type']) => void;
}) => (
  <Row gap="m">
    {(['Tabata', 'HIIT'] as const).map((type) => (
      <Pill
        content={type}
        key={type}
        onClick={() => (type !== selectedType ? onTypeChange(type) : null)}
        type={selectedType === type ? 'Primary' : 'Inactive'}
      />
    ))}
  </Row>
);

const WorkoutDescription = () => (
  <InfoBox title="How does it work?">
    Pick how long you want your users to do an exercise, how long they should
    rest in between, and how many sets they'll do. You'll also be able to define
    a longer rest at the end of each exercise. These values can be changed later
    on.
  </InfoBox>
);
